import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, Instagram } from 'lucide-react';

interface InstagramPost {
    id: string;
    media_url: string;
    permalink: string;
    caption?: string;
}

// Fallback high-quality climbing and mountain landscape mock posts
const MOCK_POSTS: InstagramPost[] = [
    {
        id: 'mock_1',
        media_url: '/images/Climbing Day/cerro-mackay-climbing-vertical.jpg',
        permalink: 'https://www.instagram.com/fisuraexpeditions/',
        caption: 'Explorando las grandes paredes de granito en Aysén. #FisuraExpeditions'
    },
    {
        id: 'mock_2',
        media_url: '/images/Climbing Day/escalada-cerro-mackay-coyhaique.jpg',
        permalink: 'https://www.instagram.com/fisuraexpeditions/',
        caption: 'Ascenso técnico en Cerro Colorado. Vistas infinitas y granito de la mejor calidad.'
    },
    {
        id: 'mock_3',
        media_url: '/images/Climbing Day/escalada-fisura-roca-aysen.jpg',
        permalink: 'https://www.instagram.com/fisuraexpeditions/',
        caption: 'Climbing Mackay - Aysén Técnico. Desafiando los límites con el Castillo de fondo.'
    },
    {
        id: 'mock_4',
        media_url: '/images/Laguna Cerro Castillo/trekking-laguna-cerro-castillo-turquesa.jpg',
        permalink: 'https://www.instagram.com/fisuraexpeditions/',
        caption: 'Laguna Cerro Castillo - Aguas turquesas y glaciares colgantes en el corazón de Aysén.'
    },
    {
        id: 'mock_5',
        media_url: '/images/Travesía Las Horquetas/travesia-las-horquetas-drone-aysen.jpg',
        permalink: 'https://www.instagram.com/fisuraexpeditions/',
        caption: 'Travesía Las Horquetas - Vistas aéreas de uno de los trekkings más salvajes del planeta.'
    },
    {
        id: 'mock_6',
        media_url: '/images/Climbing Day/rock-climbing-walls-panorama.jpg',
        permalink: 'https://www.instagram.com/fisuraexpeditions/',
        caption: 'Días de escalada en roca en los imponentes paredones de Villa Cerro Castillo.'
    }
];

export const InstagramFeed: React.FC = () => {
    const [posts, setPosts] = useState<InstagramPost[]>(MOCK_POSTS);
    const [isHovered, setIsHovered] = useState(false);
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    const showLeftArrow = posts.length > 1;
    const showRightArrow = posts.length > 1;

    // Triple the posts array to create a seamless infinite scrolling marquee
    const displayPosts = [...posts, ...posts, ...posts];

    useEffect(() => {
        const fetchInstagramFeed = async () => {
            const beholdUrl = (import.meta as any).env?.VITE_BEHOLD_URL;
            if (!beholdUrl) return;

            try {
                const response = await fetch(beholdUrl);
                if (response.ok) {
                    const data = await response.json();
                    
                    // Behold.so can return either a direct array of posts or an object containing a 'posts' array.
                    const rawPosts = Array.isArray(data) ? data : (data && Array.isArray(data.posts) ? data.posts : null);

                    if (rawPosts && rawPosts.length > 0) {
                        // Normalize posts to match our InstagramPost interface, supporting both camelCase and snake_case properties.
                        // We prioritize Behold's cached/proxied URLs (sizes.medium.mediaUrl, etc.) which don't expire.
                        const normalizedPosts: InstagramPost[] = rawPosts.slice(0, 8).map((post: any) => ({
                            id: post.id || Math.random().toString(),
                            media_url: post.sizes?.medium?.mediaUrl || post.sizes?.large?.mediaUrl || post.sizes?.full?.mediaUrl || post.mediaUrl || post.media_url || '',
                            permalink: post.permalink || 'https://www.instagram.com/fisuraexpeditions/',
                            caption: post.caption || post.prunedCaption || ''
                        }));
                        setPosts(normalizedPosts);
                    }
                }
            } catch (error) {
                console.warn('Failed to fetch live Instagram feed, using mock posts:', error);
            }
        };

        fetchInstagramFeed();
    }, []);

    // Scroll wrap listener to make marquee infinite
    const handleScrollEvent = () => {
        if (scrollContainerRef.current) {
            const container = scrollContainerRef.current;
            const children = container.children;
            if (children.length < posts.length * 3) return;

            const firstChild = children[0] as HTMLElement;
            const targetChild = children[posts.length] as HTMLElement;
            if (!firstChild || !targetChild) return;

            const oneSetScrollWidth = targetChild.offsetLeft - firstChild.offsetLeft;
            const { scrollLeft, scrollWidth, clientWidth } = container;

            // Scroll boundary wrapping for infinite loop
            // Right boundary: wrap to the left when getting close to the end
            if (scrollLeft >= scrollWidth - clientWidth - 10) {
                container.scrollLeft = scrollLeft - oneSetScrollWidth;
            }
            // Left boundary: wrap to the right when getting close to the start
            else if (scrollLeft <= 10) {
                container.scrollLeft = scrollLeft + oneSetScrollWidth;
            }
        }
    };

    useEffect(() => {
        const container = scrollContainerRef.current;
        if (container) {
            container.addEventListener('scroll', handleScrollEvent);
        }
        return () => {
            if (container) {
                container.removeEventListener('scroll', handleScrollEvent);
            }
        };
    }, [posts]);

    // Initial position: start in the middle set of duplicated posts for smooth bidirectional wrapping
    useEffect(() => {
        if (posts.length > 0) {
            const timer = setTimeout(() => {
                if (scrollContainerRef.current) {
                    const container = scrollContainerRef.current;
                    const children = container.children;
                    if (children.length >= posts.length * 3) {
                        const firstChild = children[0] as HTMLElement;
                        const targetChild = children[posts.length] as HTMLElement;
                        if (firstChild && targetChild) {
                            const oneSetScrollWidth = targetChild.offsetLeft - firstChild.offsetLeft;
                            container.scrollLeft = oneSetScrollWidth;
                        }
                    }
                }
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [posts]);

    // Auto-scroll marquee effect using requestAnimationFrame for maximum smoothness
    useEffect(() => {
        if (isHovered || posts.length <= 1) return;

        let lastTime = performance.now();
        let animationFrameId: number;
        let accumulator = 0;
        // Speed in pixels per millisecond (0.025 px/ms = 25 pixels per second, slow and elegant)
        const speed = 0.025;

        const scroll = (time: number) => {
            const delta = time - lastTime;
            lastTime = time;

            if (scrollContainerRef.current) {
                accumulator += delta * speed;
                if (accumulator >= 1) {
                    const pixelsToScroll = Math.floor(accumulator);
                    scrollContainerRef.current.scrollLeft += pixelsToScroll;
                    accumulator -= pixelsToScroll;
                }
            }
            animationFrameId = requestAnimationFrame(scroll);
        };

        animationFrameId = requestAnimationFrame(scroll);
        return () => cancelAnimationFrame(animationFrameId);
    }, [isHovered, posts]);

    const handleScroll = (direction: 'left' | 'right') => {
        if (scrollContainerRef.current) {
            const { clientWidth } = scrollContainerRef.current;
            const scrollAmount = clientWidth * 0.75;
            scrollContainerRef.current.scrollBy({
                left: direction === 'left' ? -scrollAmount : scrollAmount,
                behavior: 'smooth'
            });
        }
    };

    return (
        <div 
            className="relative w-full group selection:bg-transparent"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <style>{`
                .instagram-feed-container::-webkit-scrollbar {
                    height: 5px;
                }
                .instagram-feed-container::-webkit-scrollbar-track {
                    background: rgba(0, 0, 0, 0.05);
                    border-radius: 10px;
                }
                .instagram-feed-container::-webkit-scrollbar-thumb {
                    background: rgba(0, 212, 255, 0.3);
                    border-radius: 10px;
                    transition: background 0.3s;
                }
                .instagram-feed-container::-webkit-scrollbar-thumb:hover {
                    background: rgba(0, 212, 255, 0.7);
                }
                .instagram-feed-container {
                    scrollbar-width: thin;
                    scrollbar-color: rgba(0, 212, 255, 0.3) rgba(0, 0, 0, 0.05);
                }
            `}</style>
            {/* Left Scroll Button */}
            {showLeftArrow && (
                <button
                    onClick={() => handleScroll('left')}
                    className="absolute -left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-black/80 hover:bg-[#00d4ff] text-white hover:text-black border border-white/10 flex items-center justify-center z-30 transition-all duration-300 shadow-xl"
                    aria-label="Scroll left"
                >
                    <ChevronLeft size={24} />
                </button>
            )}

            {/* Scrollable Feed Container */}
            <div
                ref={scrollContainerRef}
                className="flex gap-4 overflow-x-auto instagram-feed-container py-4 px-2"
            >
                {displayPosts.map((post, index) => (
                    <a
                        key={`${post.id}_${index}`}
                        href={post.permalink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-shrink-0 w-[260px] sm:w-[280px] aspect-square bg-black/40 border border-black/5 rounded-sm overflow-hidden relative group/item shadow-md"
                    >
                        <img
                            src={post.media_url}
                            alt={post.caption || 'Instagram Post'}
                            className="w-full h-full object-cover transition-transform duration-700 group-hover/item:scale-105"
                            onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.src = '/images/Climbing Day/cerro-mackay-climbing-vertical.jpg';
                            }}
                        />

                        {/* Glassmorphism Hover Overlay */}
                        <div className="absolute inset-0 bg-black/75 opacity-0 group-hover/item:opacity-100 transition-all duration-300 flex flex-col justify-between p-5">
                            <div className="flex items-center justify-between text-white">
                                <span className="text-[10px] tracking-[0.2em] font-bold uppercase text-[#00d4ff] flex items-center gap-1.5">
                                    <Instagram size={14} />
                                    @fisuraexpeditions
                                </span>
                            </div>

                            <p className="text-[11px] text-gray-200 line-clamp-4 leading-relaxed font-light tracking-wide uppercase">
                                {post.caption || 'Sígannos en Instagram para ver nuestras aventuras.'}
                            </p>

                            <div className="text-[9px] font-bold text-white tracking-[0.25em] uppercase border-b border-[#00d4ff]/30 pb-1 self-start">
                                VER PUBLICACIÓN
                            </div>
                        </div>
                    </a>
                ))}
            </div>

            {/* Right Scroll Button */}
            {showRightArrow && (
                <button
                    onClick={() => handleScroll('right')}
                    className="absolute -right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-black/80 hover:bg-[#00d4ff] text-white hover:text-black border border-white/10 flex items-center justify-center z-30 transition-all duration-300 shadow-xl"
                    aria-label="Scroll right"
                >
                    <ChevronRight size={24} />
                </button>
            )}
        </div>
    );
};
